import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { REVIEW_TYPE_LABELS } from '../constants/reviewType';
import '../css/Dialog.css';

/**
 * Confirmation dialog for marking a performance review as COMPLETED or SKIPPED.
 *
 * Props:
 *  open          – boolean
 *  status        – 'COMPLETED' | 'SKIPPED'
 *  row           – PerformanceReviewResponseDto row object
 *  comment       – string (controlled)
 *  onCommentChange – (value: string) => void
 *  onConfirm     – () => void
 *  onClose       – () => void
 *  fmtDate       – (dateStr) => string
 */
const ReviewActionDialog = ({
  open,
  status,
  row,
  comment,
  onCommentChange,
  onConfirm,
  onClose,
  fmtDate,
}) => {
  const isComplete = status === 'COMPLETED';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{ paper: { className: 'modern-dialog' } }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle className={`modern-dialog-title${isComplete ? '' : ' delete-header'}`}>
        <span className="modern-dialog-title-text">
          {isComplete ? 'Mark as Completed' : 'Skip Review'}
        </span>
        <IconButton aria-label="close" onClick={onClose} className="modern-dialog-close-btn">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="modern-dialog-content">
        {row && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {isComplete
                ? `Mark the performance review for ${row.userName ?? 'this employee'} as completed?`
                : `Skip the performance review for ${row.userName ?? 'this employee'}? This cannot be undone.`
              }
            </Typography>

            {/* Review type + trigger date summary */}
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}
            >
              {REVIEW_TYPE_LABELS[row.reviewType] ?? row.reviewType}
              {row.triggerDate ? `  ·  Due ${fmtDate(row.triggerDate)}` : ''}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Optional comment */}
            <TextField
              id="review-action-comment"
              label="Comment (Optional)"
              multiline
              rows={3}
              fullWidth
              size="small"
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              placeholder={isComplete ? 'Add review notes…' : 'Reason for skipping…'}
            />
          </>
        )}
      </DialogContent>

      <DialogActions className="modern-dialog-actions">
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
        >
          Cancel
        </Button>
        <Button
          id={`review-confirm-${status?.toLowerCase()}-btn`}
          onClick={onConfirm}
          variant="contained"
          color={isComplete ? 'success' : 'warning'}
          disableElevation
          startIcon={
            isComplete
              ? <CheckCircleOutlineIcon fontSize="small" />
              : <RemoveCircleOutlineIcon fontSize="small" />
          }
        >
          {isComplete ? 'Yes, Complete' : 'Yes, Skip'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewActionDialog;
